<template>
  <div class="tasks-page">
    <PageHeader
      :title="$t('tasks_view.title')"
      :description="$t('tasks_view.description')"
      size="compact"
      break-mobile
    >
      <template #actions>
        <Btn variant="tertiary" size="sm" icon="redo" :loading="loading" @click="fetchTasks">
          {{ $t('common.refresh') }}
        </Btn>
      </template>
    </PageHeader>

    <div class="tasks-page__content">
      <JourneyActionsPanel :members="members" />

      <div class="table-container">
        <div class="table-heading">
          <div class="table-heading__title">
            {{ $t('tasks_view.title') }}
            <span class="table-heading__count">{{ pagination.total || 0 }}</span>
          </div>
          <div class="table-heading__actions">
            <div class="table-heading__search">
              <TextInput
                id="tasks-search"
                :model-value="searchQuery"
                :placeholder="$t('tasks_view.search_placeholder')"
                @update:model-value="handleSearch"
              />
            </div>

            <DropdownMenu class="tasks-filter">
              <template #trigger>
                <Btn
                  variant="outlined"
                  size="sm"
                  icon="filter"
                  :class="{ 'filter-active': hasActiveStatusFilter }"
                >
                  {{ $t('tasks_view.status_label') }}
                  <span v-if="statusFilterCount" class="filter-count">{{ statusFilterCount }}</span>
                </Btn>
              </template>
              <template #items>
                <div class="tasks-filter__panel">
                  <div class="tasks-filter__section">
                    <div class="tasks-filter__header">
                      <span>{{ $t('tasks_view.status_label') }}</span>
                      <button type="button" @click.stop="selectAllStatuses">
                        {{ $t('tasks_view.select_all') }}
                      </button>
                    </div>
                    <label
                      v-for="status in statusOptions"
                      :key="status.value"
                      class="tasks-filter__option"
                    >
                      <input
                        v-model="selectedStatuses"
                        type="checkbox"
                        :value="status.value"
                        @change="applyFilters"
                      />
                      <span>{{ status.label }}</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    class="tasks-filter__reset"
                    :disabled="!hasActiveStatusFilter"
                    @click="resetStatusFilter"
                  >
                    {{ $t('tasks_view.reset_filters') }}
                  </button>
                </div>
              </template>
            </DropdownMenu>

            <DropdownMenu class="tasks-filter">
              <template #trigger>
                <Btn
                  variant="outlined"
                  size="sm"
                  icon="user"
                  :class="{ 'filter-active': hasActiveAssigneeFilter }"
                >
                  {{ $t('tasks_view.assigned_to_label') }}
                  <span v-if="selectedAssigneeIds.length" class="filter-count">
                    {{ selectedAssigneeIds.length }}
                  </span>
                </Btn>
              </template>
              <template #items>
                <div class="tasks-filter__panel">
                  <div class="tasks-filter__section">
                    <div class="tasks-filter__header">
                      <span>{{ $t('tasks_view.assigned_to_label') }}</span>
                      <button type="button" @click.stop="clearAssigneeFilter">
                        {{ $t('tasks_view.assigned_to_all') }}
                      </button>
                    </div>
                    <label
                      v-for="member in memberOptions"
                      :key="member.value"
                      class="tasks-filter__option"
                    >
                      <input
                        v-model="selectedAssigneeIds"
                        type="checkbox"
                        :value="member.value"
                        @change="applyFilters"
                      />
                      <span>{{ member.label }}</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    class="tasks-filter__reset"
                    :disabled="!hasActiveAssigneeFilter"
                    @click="clearAssigneeFilter"
                  >
                    {{ $t('tasks_view.reset_filters') }}
                  </button>
                </div>
              </template>
            </DropdownMenu>
          </div>
        </div>

        <div v-if="selectedItems.length > 0" class="bulk-bar tasks-page__bulk-bar">
          <select v-model="bulkSelectedAction" class="tasks-page__bulk-select">
            <option value="complete">{{ $t('tasks_view.bulk_complete') }}</option>
            <option value="assign">{{ $t('tasks_view.bulk_assign') }}</option>
            <option value="delete">{{ $t('common.delete') }}</option>
          </select>
          <select
            v-if="bulkSelectedAction === 'assign'"
            v-model="bulkAssignUserId"
            class="tasks-page__bulk-select"
          >
            <option value="">{{ $t('tasks_view.bulk_assign_placeholder') }}</option>
            <option v-for="member in memberOptions" :key="member.value" :value="member.value">
              {{ member.label }}
            </option>
          </select>
          <Btn
            :variant="bulkActionButtonVariant"
            size="sm"
            :disabled="!canRunBulkAction"
            :loading="bulkLoading"
            @click="runBulkAction"
          >
            {{ bulkActionButtonLabel }}
          </Btn>
        </div>

        <EasyDataTable
          :headers="headers"
          :items="formattedTasks"
          :loading="loading"
          :server-items-length="pagination.total"
          v-model:server-options="serverOptions"
          v-model:items-selected="selectedItems"
          buttons-pagination
          @click-row="openTask"
          table-class-name="custom-table"
        >
          <template #empty>
            <div class="tasks-page__empty">
              <EmptyState
                icon="check-square"
                :title="$t('tasks_view.empty_title')"
                :description="$t('tasks_view.empty_description')"
              />
            </div>
          </template>

          <template #item-title="item">
            <button type="button" class="tasks-page__task-link" @click.stop="openTask(item)">
              <span class="tasks-page__task-icon">
                <BaseIcon
                  :name="item.status === 'completed' ? 'check-square' : 'circle-dot'"
                  size="15"
                />
              </span>
              <span class="tasks-page__task-copy">
                <strong>{{ item.title }}</strong>
                <small v-if="item.relatedLabel">{{ item.relatedLabel }}</small>
              </span>
            </button>
          </template>

          <template #item-due_date="item">
            <span :class="{ 'tasks-page__due--overdue': item.isOverdue }">
              {{ item.dueDateLabel }}
            </span>
          </template>

          <template #item-priority="item">
            <span
              class="tasks-page__priority"
              :class="`tasks-page__priority--${item.priority || 'low'}`"
            >
              {{ item.priorityLabel }}
            </span>
          </template>
        </EasyDataTable>
      </div>
    </div>

  </div>
</template>

<script>
  import { ref, reactive, computed, watch, onMounted, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import { taskService } from '@/services/taskService';
  import OrganizationService from '@/services/organizationService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUserStore } from '@/store/userStore';
  import { useUrlPagination } from '@/composables/useUrlPagination';
  import { useTaskDetail } from '@/composables/useTaskDetail';

  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import JourneyActionsPanel from '@/components/tasks/JourneyActionsPanel.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'TasksPage',

    components: {
      EasyDataTable,
      PageHeader,
      Btn,
      EmptyState,
      TextInput,
      DropdownMenu,
      JourneyActionsPanel,
      BaseIcon,
    },

    setup() {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const userStore = useUserStore();
      const { openTaskDetail } = useTaskDetail();
      const organizationService = new OrganizationService();
      const { serverOptions } = useUrlPagination(25);

      const tasks = ref([]);
      const members = ref([]);
      const selectedItems = ref([]);
      const loading = ref(false);
      const bulkLoading = ref(false);
      const bulkAction = ref(null);
      const bulkSelectedAction = ref('complete');
      const bulkAssignUserId = ref('');
      const searchQuery = ref('');
      const searchTimeout = ref(null);
      const statusOptions = [
        { value: 'pending', label: $t('tasks_view.status_pending') },
        { value: 'in_progress', label: $t('tasks_view.status_in_progress') },
        { value: 'completed', label: $t('tasks_view.status_completed') },
      ];
      const defaultStatusValues = ['pending', 'in_progress'];
      const selectedStatuses = ref([...defaultStatusValues]);
      // Default: only show tasks assigned to the logged-in user
      const selectedAssigneeIds = ref(
        userStore.user?.id ? [userStore.user.id] : []
      );
      const pagination = reactive({
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 1,
      });

      const headers = [
        { text: $t('tasks_view.fields.name'), value: 'title' },
        { text: $t('tasks_view.fields.due_date'), value: 'due_date' },
        { text: $t('tasks_view.fields.assigned_to'), value: 'assignedToLabel' },
        { text: $t('tasks_view.fields.priority'), value: 'priority' },
      ];

      const memberOptions = computed(() =>
        members.value.map((member) => {
          const user = member.user || member;
          const id = user?.id || member.user_id || member.id;
          const label =
            [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
            user?.name ||
            user?.email ||
            member.email ||
            id;

          return { value: id, label };
        }).filter((member) => member.value)
      );

      const hasActiveStatusFilter = computed(() => {
        if (selectedStatuses.value.length !== defaultStatusValues.length) return true;
        return selectedStatuses.value.some((status) => !defaultStatusValues.includes(status));
      });

      const hasActiveAssigneeFilter = computed(() => selectedAssigneeIds.value.length > 0);

      const statusFilterCount = computed(() => {
        if (!hasActiveStatusFilter.value) return 0;
        return selectedStatuses.value.length;
      });

      const normalizePagination = (responsePagination = {}) => {
        pagination.page = responsePagination.page || serverOptions.value.page;
        pagination.limit = responsePagination.limit || serverOptions.value.rowsPerPage;
        pagination.total = responsePagination.total || tasks.value.length;
        pagination.totalPages =
          responsePagination.totalPages ||
          responsePagination.total_pages ||
          responsePagination.last_page ||
          Math.max(1, Math.ceil(pagination.total / pagination.limit));
      };

      const buildParams = () => {
        const params = {
          page: serverOptions.value.page,
          limit: serverOptions.value.rowsPerPage,
        };

        if (searchQuery.value.trim()) {
          params.search = searchQuery.value.trim();
        }

        if (selectedStatuses.value.length > 0) {
          params.status = selectedStatuses.value.join(',');
        }

        if (selectedAssigneeIds.value.length > 0) {
          params.assigned_to = selectedAssigneeIds.value.join(',');
        }

        return params;
      };

      const fetchTasks = async () => {
        loading.value = true;
        try {
          const response = await taskService.getTasks(buildParams());
          tasks.value = response.tasks || [];
          normalizePagination(response.pagination);
        } catch (error) {
          console.error('Failed to load tasks:', error);
          toastStore.addToast({
            type: 'error',
            message: $t('tasks_view.load_failed'),
          });
        } finally {
          loading.value = false;
        }
      };

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;

        try {
          const response = await organizationService.getMembers(organizationId);
          members.value = Array.isArray(response) ? response : response?.data || [];
        } catch (error) {
          console.error('Failed to load task assignee filter options:', error);
          members.value = [];
        }
      };

      const formatDueDate = (task) => {
        if (!task.due_date) return $t('tasks_view.no_due_date');
        const due = dayjs(`${task.due_date}T${task.due_time || '23:59:59'}`);
        const date = due.format('DD MMM YYYY');
        return task.due_time ? `${date} ${due.format('HH:mm')}` : date;
      };

      const isOverdue = (task) => {
        if (!task.due_date || task.status === 'completed') return false;
        return dayjs(`${task.due_date}T${task.due_time || '23:59:59'}`).isBefore(dayjs());
      };

      const getPersonName = (person) => {
        if (!person) return '';
        return (
          [person.first_name, person.last_name].filter(Boolean).join(' ').trim() ||
          person.name ||
          person.email ||
          ''
        );
      };

      const getAssigneeName = (task) => {
        return (
          getPersonName(task.assignee) ||
          getPersonName(task.assigned_to_user) ||
          getPersonName(task.deal?.owner) ||
          getPersonName(task.deal?.assigned_to_user) ||
          task.assigned_to ||
          $t('tasks_view.unassigned')
        );
      };

      const getRelatedLabel = (task) => {
        if (task.project_id || task.project?.id) {
          const number = task.project?.project_number;
          const title = task.project?.title || task.lead?.name || '';
          const label = number ? `${number}${title ? ` · ${title}` : ''}` : title;
          return `${$t('tasks_view.home_project')}${label ? ` · ${label}` : ''}`;
        }
        if (task.deal?.title) {
          return `${$t('tasks_view.home_deal')} · ${task.deal.title}`;
        }
        if (task.lead?.name) return task.lead.name;
        return $t('tasks_view.home_general');
      };

      const getPriorityLabel = (priority) => {
        const labels = {
          high: $t('deals.priority_high'),
          medium: $t('deals.priority_medium'),
          low: $t('deals.priority_low'),
        };
        return labels[priority] || priority || $t('deals.priority_low');
      };

      const formattedTasks = computed(() =>
        tasks.value.map((task) => ({
          ...task,
          dueDateLabel: formatDueDate(task),
          assignedToLabel: getAssigneeName(task),
          relatedLabel: getRelatedLabel(task),
          priorityLabel: getPriorityLabel(task.priority),
          isOverdue: isOverdue(task),
        }))
      );

      const selectedTaskIds = computed(() =>
        selectedItems.value.map((task) => task.id).filter(Boolean)
      );

      const canRunBulkAction = computed(() => {
        if (selectedTaskIds.value.length === 0 || !bulkSelectedAction.value) return false;
        if (bulkSelectedAction.value === 'assign') return Boolean(bulkAssignUserId.value);
        return true;
      });

      const bulkActionButtonVariant = computed(() =>
        bulkSelectedAction.value === 'delete' ? 'danger' : 'primary'
      );

      const bulkActionButtonLabel = computed(() => {
        const count = selectedTaskIds.value.length;
        const labels = {
          complete: $t('tasks_view.bulk_button_complete', { count }),
          assign: $t('tasks_view.bulk_button_assign', { count }),
          delete: $t('tasks_view.bulk_button_delete', { count }),
        };
        return labels[bulkSelectedAction.value] || $t('tasks_view.bulk_action_placeholder');
      });

      const openTask = (task) => {
        openTaskDetail(task, { onUpdated: fetchTasks });
      };

      const clearSelection = () => {
        selectedItems.value = [];
        bulkSelectedAction.value = 'complete';
        bulkAssignUserId.value = '';
      };

      const runBulkAction = () => {
        const actions = {
          complete: completeSelectedTasks,
          assign: assignSelectedTasks,
          delete: confirmDeleteSelectedTasks,
        };
        actions[bulkSelectedAction.value]?.();
      };

      const completeSelectedTasks = async () => {
        const ids = selectedTaskIds.value;
        if (ids.length === 0) return;

        bulkLoading.value = true;
        bulkAction.value = 'complete';
        try {
          await taskService.bulkComplete(ids);
          toastStore.addToast({
            type: 'success',
            message: $t('tasks_view.bulk_complete_success'),
          });
          clearSelection();
          await fetchTasks();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('tasks_view.bulk_action_failed'),
          });
        } finally {
          bulkLoading.value = false;
          bulkAction.value = null;
        }
      };

      const assignSelectedTasks = async () => {
        const ids = selectedTaskIds.value;
        if (ids.length === 0 || !bulkAssignUserId.value) return;

        bulkLoading.value = true;
        bulkAction.value = 'assign';
        try {
          await taskService.bulkAssign(ids, bulkAssignUserId.value);
          toastStore.addToast({
            type: 'success',
            message: $t('tasks_view.bulk_assign_success'),
          });
          clearSelection();
          await fetchTasks();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('tasks_view.bulk_action_failed'),
          });
        } finally {
          bulkLoading.value = false;
          bulkAction.value = null;
        }
      };

      const confirmDeleteSelectedTasks = () => {
        const count = selectedTaskIds.value.length;
        if (count === 0) return;

        const modalId = uiStore.addModal(false, {
          title: $t('tasks_view.bulk_delete_confirm_title'),
          description: $t('tasks_view.bulk_delete_confirm_description', { count }),
          submitText: $t('tasks_view.bulk_button_delete', { count }),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            await deleteSelectedTasks();
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const deleteSelectedTasks = async () => {
        const ids = selectedTaskIds.value;
        if (ids.length === 0) return;

        bulkLoading.value = true;
        bulkAction.value = 'delete';
        try {
          await taskService.bulkDelete(ids);
          toastStore.addToast({
            type: 'success',
            message: $t('tasks_view.bulk_delete_success'),
          });
          clearSelection();
          await fetchTasks();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: $t('tasks_view.bulk_action_failed'),
          });
        } finally {
          bulkLoading.value = false;
          bulkAction.value = null;
        }
      };

      const applyFilters = () => {
        if (selectedStatuses.value.length === 0) {
          selectedStatuses.value = [...defaultStatusValues];
        }

        if (serverOptions.value.page === 1) {
          fetchTasks();
          return;
        }
        serverOptions.value.page = 1;
      };

      const resetFilters = () => {
        selectedStatuses.value = [...defaultStatusValues];
        selectedAssigneeIds.value = [];
        applyFilters();
      };

      const resetStatusFilter = () => {
        selectedStatuses.value = [...defaultStatusValues];
        applyFilters();
      };

      const resetAssigneeFilter = () => {
        selectedAssigneeIds.value = [];
        applyFilters();
      };

      const selectAllStatuses = () => {
        selectedStatuses.value = statusOptions.map((status) => status.value);
        applyFilters();
      };

      const clearAssigneeFilter = () => {
        resetAssigneeFilter();
      };

      const handleSearch = (value) => {
        searchQuery.value = value;
        clearTimeout(searchTimeout.value);
        searchTimeout.value = setTimeout(() => {
          if (serverOptions.value.page === 1) {
            fetchTasks();
            return;
          }
          serverOptions.value.page = 1;
        }, 300);
      };

      watch(
        serverOptions,
        () => {
          fetchTasks();
        },
        { deep: true }
      );

      onMounted(async () => {
        await Promise.all([fetchTasks(), fetchMembers()]);
      });

      return {
        tasks,
        members,
        selectedItems,
        loading,
        bulkLoading,
        bulkAction,
        bulkSelectedAction,
        bulkAssignUserId,
        canRunBulkAction,
        bulkActionButtonVariant,
        bulkActionButtonLabel,
        searchQuery,
        statusOptions,
        selectedStatuses,
        selectedAssigneeIds,
        memberOptions,
        hasActiveStatusFilter,
        hasActiveAssigneeFilter,
        statusFilterCount,
        pagination,
        serverOptions,
        headers,
        formattedTasks,
        fetchTasks,
        handleSearch,
        applyFilters,
        resetFilters,
        resetStatusFilter,
        resetAssigneeFilter,
        selectAllStatuses,
        clearAssigneeFilter,
        openTask,
        clearSelection,
        runBulkAction,
        completeSelectedTasks,
        assignSelectedTasks,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .tasks-page {
    padding: 24px;

    @include lg-down {
      padding: 12px;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-top: 1rem;
    }

    &__bulk-bar {
      display: flex;
      align-items: center;
      gap: 0.625rem;
      padding: 0.875rem 1rem;
    }

    &__bulk-select {
      height: 35px;
      min-width: 180px;
      padding: 0 0.625rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: #ffffff;
      color: var(--slate-700);
      font-size: 0.875rem;
      line-height: 1.5;

      &:focus {
        outline: none;
        border-color: var(--brand-400);
      }
    }

    &__empty {
      padding: 2rem;
    }

    &__task-link {
      display: inline-flex;
      align-items: center;
      gap: 0.75rem;
      min-width: 0;
      max-width: 100%;
      border: 0;
      background: transparent;
      color: inherit;
      font: inherit;
      text-align: left;
      cursor: pointer;
    }

    &__task-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      background: var(--brand-50);
      color: var(--brand-600);
      flex-shrink: 0;
    }

    &__task-copy {
      min-width: 0;

      strong,
      small {
        display: block;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      strong {
        color: var(--slate-900);
        font-size: 0.875rem;
        font-weight: 600;
      }

      small {
        margin-top: 0.125rem;
        color: var(--slate-500);
        font-size: 0.75rem;
      }
    }

    &__due--overdue {
      color: var(--red-600);
      font-weight: 600;
    }

    &__priority {
      display: inline-flex;
      align-items: center;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      background: var(--slate-100);
      color: var(--slate-600);
      font-size: 0.75rem;
      font-weight: 700;

      &--high {
        background: var(--red-50);
        color: var(--red-700);
      }

      &--medium {
        background: var(--amber-50);
        color: var(--amber-700);
      }
    }
  }

  .tasks-filter {
    &__panel {
      min-width: 260px;
      padding: 0.75rem;
    }

    &__section {
      padding-bottom: 0.75rem;
      margin-bottom: 0.75rem;
      border-bottom: 1px solid var(--slate-100);

      &:last-of-type {
        margin-bottom: 0;
      }
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 0.375rem;
      color: var(--slate-700);
      font-size: 0.8125rem;
      font-weight: 600;

      button {
        border: 0;
        background: transparent;
        color: var(--brand-600);
        font-size: 0.75rem;
        font-weight: 600;
        cursor: pointer;

        &:hover {
          color: var(--brand-700);
        }
      }
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem;
      border-radius: 0.375rem;
      color: var(--slate-700);
      font-size: 0.875rem;
      cursor: pointer;

      &:hover {
        background: var(--slate-50);
      }

      input[type='checkbox'] {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }

    &__reset {
      width: 100%;
      margin-top: 0.75rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: #ffffff;
      color: var(--slate-600);
      font-size: 0.8125rem;
      font-weight: 600;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      &:not(:disabled):hover {
        border-color: var(--slate-300);
        color: var(--slate-900);
      }
    }
  }

</style>
